import { formatRupiah } from "../lib/format";
import { Lightning, Warning, PencilSimple, Tag } from "@phosphor-icons/react";

/**
 * Render one Tindakan result table.
 * If `editable` is true, a "Diskon (%)" input column appears per row.
 * The parent handles the discount recomputation via `onEditDiskon(itemIndex, pct)`.
 */
export default function ResultTable({
  breakdown,
  index,
  editable = false,
  onEditDiskon,
}) {
  const { kategori, kelas, items, subtotal, nama_tindakan } = breakdown;
  return (
    <div
      className="print-card bg-white border border-slate-200 rounded shadow-sm"
      data-testid={`result-table-tindakan-${index + 1}`}
    >
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
            Tindakan {index + 1}
            {editable && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] normal-case bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                <PencilSimple size={10} weight="fill" /> mode edit
              </span>
            )}
          </div>
          <div className="text-base md:text-lg font-semibold text-slate-900">
            {nama_tindakan || kategori}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Kategori: <span className="font-medium text-slate-700">{kategori}</span>{" "}
            · <span className="font-medium text-slate-700">{kelas}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">
            Subtotal
          </div>
          <div
            className="text-xl font-bold tabular-nums text-blue-900"
            data-testid={`subtotal-tindakan-${index + 1}`}
          >
            {formatRupiah(subtotal)}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b border-slate-200 whitespace-nowrap">
                Nama Instrumen
              </th>
              <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b border-slate-200 whitespace-nowrap">
                Kelas
              </th>
              <th className="bg-slate-50 text-slate-600 font-semibold text-left p-3 border-b border-slate-200 whitespace-nowrap">
                Tipe Tindakan Operasi
              </th>
              {editable && (
                <th className="bg-amber-50 text-amber-800 font-semibold text-right p-3 border-b border-amber-200 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1 justify-end">
                    <Tag size={12} weight="fill" /> Diskon (%)
                  </span>
                </th>
              )}
              <th className="bg-slate-50 text-slate-600 font-semibold text-right p-3 border-b border-slate-200 whitespace-nowrap">
                Tarif
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const isCito = (it.kondisi || "").includes("CITO");
              const isPenyulit = (it.kondisi || "").includes("PENYULIT");
              const tarifAsli = it.tarif_asli != null ? it.tarif_asli : it.tarif;
              const diskonPct = it.diskon_pct || 0;
              const hasDiskon = editable && diskonPct > 0;
              return (
                <tr
                  key={i}
                  className="hover:bg-slate-50 transition-colors"
                  data-testid={`row-tindakan-${index + 1}-item-${i}`}
                >
                  <td className="p-3 border-b border-slate-100 align-top text-slate-800">
                    {it.nama_instrumen}
                    {it.is_custom && (
                      <span
                        className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-50 text-violet-700 border border-violet-200"
                        data-testid={`badge-custom-t${index + 1}-i${i}`}
                      >
                        Tambahan
                      </span>
                    )}
                  </td>
                  <td className="p-3 border-b border-slate-100 align-top text-slate-700 whitespace-nowrap">
                    {it.kelas}
                  </td>
                  <td className="p-3 border-b border-slate-100 align-top text-slate-700 text-xs">
                    <div className="flex flex-wrap gap-1 items-center">
                      <span>{it.kondisi}</span>
                      {isCito && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#FEF2F2] text-[#DC2626] border border-red-200">
                          <Lightning size={10} weight="fill" /> CITO
                        </span>
                      )}
                      {isPenyulit && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#FFFBEB] text-[#D97706] border border-amber-200">
                          <Warning size={10} weight="fill" /> PENYULIT
                        </span>
                      )}
                    </div>
                  </td>
                  {editable && (
                    <td className="p-3 border-b border-slate-100 align-top text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={diskonPct}
                          onChange={(e) =>
                            onEditDiskon &&
                            onEditDiskon(i, e.target.value)
                          }
                          className="w-20 border border-amber-300 bg-amber-50 rounded px-2 py-1 text-sm text-right tabular-nums focus:ring-2 focus:ring-amber-400"
                          data-testid={`edit-diskon-t${index + 1}-i${i}`}
                        />
                        <span className="text-xs text-amber-700">%</span>
                      </div>
                    </td>
                  )}
                  <td className="p-3 border-b border-slate-100 align-top text-right whitespace-nowrap">
                    {hasDiskon ? (
                      <div className="flex flex-col items-end">
                        <span className="text-[11px] text-slate-400 line-through tabular-nums">
                          {formatRupiah(tarifAsli)}
                        </span>
                        <span
                          className="tabular-nums font-semibold text-emerald-700"
                          data-testid={`tarif-akhir-t${index + 1}-i${i}`}
                        >
                          {formatRupiah(it.tarif)}
                        </span>
                      </div>
                    ) : (
                      <span
                        className="tabular-nums font-medium text-slate-900"
                        data-testid={`tarif-akhir-t${index + 1}-i${i}`}
                      >
                        {formatRupiah(it.tarif)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-blue-50 font-bold text-blue-900 border-t-2 border-blue-200">
              <td colSpan={editable ? 4 : 3} className="p-3 text-right">
                Subtotal Tindakan {index + 1}
              </td>
              <td className="p-3 text-right tabular-nums">{formatRupiah(subtotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
